import FileSaver from "file-saver"
import React from "react"
import { Modal, Form, Col, Row, Button, Container, ProgressBar } from "react-bootstrap"
import { toast } from "react-toastify"
import { Observable, Subject, Subscription, timer } from "rxjs"
import { scan, startWith, switchMap, takeWhile } from "rxjs/operators"
import { APIError } from "../../api/ObservableFromFetch"
import { MaxFileNameLength, MaxImageFileSize } from "../../config/Constants"
import ActionButton from "../ActionButton"

interface CSVUploadModalProps {
  show: boolean
  title: string
  templateCSVUrl: string
  children?: React.ReactNode
  onCancel: () => void
  onStartUpload: (fileName: string, fileContent: string) => Observable<string>
  onUploaded: () => void
}

interface CSVUploadModalState {
  fileInputKey: string
  fileName?: string
  fileContent?: string
  isFileNameValid: boolean
  isFileSizeValid: boolean
  uploadPercentage?: number
  uploading: boolean
  uploadStatus?: boolean
}

export default class CSVUploadModal extends React.PureComponent<CSVUploadModalProps, CSVUploadModalState> {
  state: CSVUploadModalState = {
    fileInputKey: "fileInputKey",
    fileName: undefined,
    fileContent: undefined,
    isFileNameValid: true,
    isFileSizeValid: true,
    uploadPercentage: undefined,
    uploading: false
  }

  fileUploadProgressSubscription?: Subscription
  fileUploadSubscription?: Subscription
  fileUploadProgress$ = new Subject()
  file = React.createRef<HTMLInputElement>()

  componentDidMount() {
    this.fileUploadProgressSubscription = this.fileUploadProgress$.pipe(
      switchMap(value =>
        timer(0, 300).pipe(
          startWith(0),
          scan((acc, curr) => acc + 5),
          takeWhile(value => value <= 95 && this.state.uploadStatus === undefined)
        ))
    ).subscribe(
      (value: number) => this.setState({ uploadPercentage: value })
    )
  }

  componentWillUnmount() {
    this.fileUploadProgressSubscription && this.fileUploadProgressSubscription.unsubscribe()
    this.fileUploadSubscription && this.fileUploadSubscription.unsubscribe()
  }

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]
    if (!file) {
      return
    }
    const fileName = file.name
    const isFileNameValid = fileName.length < MaxFileNameLength
    const isFileSizeValid = file.size < MaxImageFileSize
    this.setState({ isFileNameValid: isFileNameValid, isFileSizeValid: isFileSizeValid })
    if (isFileNameValid && isFileSizeValid) {
      const ext = file.name.substring(file.name.lastIndexOf('.') + 1)
      if (ext && "csv" === ext.toLowerCase()) {
        var reader = new FileReader()
        const self = this
        reader.onload = function (e) {
          const result = e.target?.result as string || ""
          const stringLength = result.indexOf('base64') + 7
          const fileContent = result.slice(stringLength, result.length)
          if (fileContent) {
            self.setState({ fileName: file.name, fileContent: fileContent, uploadStatus: undefined, uploadPercentage: undefined })
          } else {
            toast.error("CSV file is empty.")
          }
        }
        reader.onerror = function (error) { toast.error(error) }
        reader.readAsDataURL(file)
      } else {
        toast.error("Please upload only text documents. Download the Example Spreadsheet as a starter.")
      }
    }
  }

  handleUpload = () => {
    this.fileUploadSubscription && this.fileUploadSubscription.unsubscribe()
    this.setState({ uploading: true, uploadStatus: undefined })
    this.fileUploadSubscription = this.props.onStartUpload(this.state.fileName || "", this.state.fileContent || "").subscribe(
      (data: string) => {
        this.setState({ uploadStatus: true, uploadPercentage: 100, uploading: false })
        this.export(data, this.state.fileName || "data.csv")
        this.props.onUploaded()
      },
      error => this.handleAPIError(error, { uploadStatus: false, uploading: false })
    )
    this.fileUploadProgress$.next()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  export(csv: string, fileName: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, fileName)
    toast.success("CSV file downloaded successfully.")
  }

  render() {
    const { uploading, fileName, fileContent, isFileNameValid, isFileSizeValid, uploadPercentage, uploadStatus } = this.state
    const disableUpload = !fileContent || !isFileNameValid || !isFileSizeValid || uploadPercentage !== undefined
    return (
      <Modal backdrop="static" show={this.props.show} size="lg">
        <Modal.Header>
          <Modal.Title className="text-uppercase">{this.props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid>
            <Row>
              <Col lg={8}>{this.props.children}</Col>
              <Col lg={4} className="text-right">
                <Button variant="primary" href={this.props.templateCSVUrl}>Download Example<br />Spreadsheet</Button>
              </Col>
            </Row>
            <Row className="form-group">
              <Col lg={8}><Form.Control type="text" value={fileName || ""} readOnly className="bg-white border-0 shadow-none" /></Col>
              <Col lg={4} className="text-right">
                <input type="file" ref={this.file} accept=".csv" onChange={this.handleFileSelect} style={{ display: 'none' }} />
                <Button variant="secondary" onClick={() => { this.file && this.file.current && this.file.current.click() }}>Browse</Button>
              </Col>
            </Row>
            {!isFileNameValid && <Row><Col className="text-center error-message">The file name should be less than 200 characters. Please rename the file.</Col></Row>}
            {!isFileSizeValid && <Row><Col className="text-center error-message">Please select file of size maximum 1 MB</Col></Row>}
            {!!fileContent && !!uploadPercentage && <>
              <Row className="form-group">
                <Col lg={{ span: 7, order: 2, offset: 3 }}>
                  <ProgressBar now={uploadPercentage} label={uploadPercentage && uploadPercentage < 100 ? `${uploadPercentage}%` : "Complete"} />
                </Col>
              </Row>
            </>}
            {uploadStatus === false && <Row><Col className="text-center error-message">Error Occurred</Col></Row>}
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.props.onCancel} className="mr-2">Cancel</Button>
          <ActionButton variant="primary" disabled={disableUpload} onClick={this.handleUpload} spin={uploading} disableOnSpin>Upload Spreadsheet</ActionButton>
        </Modal.Footer>
      </Modal>
    )
  }
}
